/**
 * ProductDateRangeFilter — simpler replacement for the per-field
 * (startDate / endDate) date range pickers.
 *
 * UX: two pickers — "Start" and "End". A product is shown when its
 * [startDate, endDate] interval OVERLAPS the user's [Start, End] range —
 * i.e. the product is active at any point inside the picked window. This
 * matches the existing Month filter's mental model.
 *
 * Implementation: emits two server-side filter items that ride along the
 * existing column-filter pipeline:
 *   { field: 'startDate', operator: 'onOrBefore', value: <userEnd>   }
 *   { field: 'endDate',   operator: 'onOrAfter',  value: <userStart> }
 * These two together encode the overlap predicate via the AND that the
 * backend already applies across filter items. Items are tagged with the
 * `DATE_RANGE_PREFIX` id so we can find / replace / clear them cleanly
 * without touching unrelated filters (e.g. the month range).
 */
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ClearIcon from '@mui/icons-material/Clear'
import DateRangeIcon from '@mui/icons-material/DateRange'
import { Box, FormControl, IconButton, Typography, useTheme } from '@mui/material'
import { GridFilterModel } from '@mui/x-data-grid-premium'
import CustomFiltersModal from 'components/DataGrid/components/CustomFiltersModal'
import CustomFiltersMenu from 'components/ui/custom-filters-menu-button'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs, { Dayjs } from 'dayjs'
import React, { useMemo, useState } from 'react'

export const DATE_RANGE_PREFIX = 'daterange-'
const ITEM_ID_ACTIVE_TO = `${DATE_RANGE_PREFIX}active-to` // startDate <= userEnd
const ITEM_ID_ACTIVE_FROM = `${DATE_RANGE_PREFIX}active-from` // endDate   >= userStart

export interface DateRangeValue {
  start: string | null
  end: string | null
}

interface ProductDateRangeFilterProps {
  filterModel: GridFilterModel
  onChange: (range: DateRangeValue) => void
  isOpen: boolean
  anchorEl: HTMLElement | null
  onOpenFilters: (event: React.MouseEvent<HTMLElement>) => void
  onClose: () => void
}

const ProductDateRangeFilter: React.FC<ProductDateRangeFilterProps> = ({
  filterModel,
  onChange,
  isOpen,
  anchorEl,
  onOpenFilters,
  onClose,
}) => {
  const theme = useTheme()

  // Seed local state from existing filter items so the popover stays in sync
  // when the user closes and re-opens it (or when persisted filters load).
  const seeded = useMemo(() => {
    const items = filterModel?.items ?? []
    const userStart = items.find(i => i.id === ITEM_ID_ACTIVE_FROM)?.value as string | undefined
    const userEnd = items.find(i => i.id === ITEM_ID_ACTIVE_TO)?.value as string | undefined
    return {
      start: userStart ? dayjs(userStart) : null,
      end: userEnd ? dayjs(userEnd) : null,
    }
  }, [filterModel])

  const [start, setStart] = useState<Dayjs | null>(seeded.start)
  const [end, setEnd] = useState<Dayjs | null>(seeded.end)

  // Toolbar badge — count active items belonging to this filter only.
  const count = useMemo(
    () =>
      (filterModel?.items ?? []).filter(i => i.id === ITEM_ID_ACTIVE_FROM || i.id === ITEM_ID_ACTIVE_TO).length,
    [filterModel]
  )

  const emit = (nextStart: Dayjs | null, nextEnd: Dayjs | null): void => {
    onChange({
      start: nextStart ? nextStart.format('YYYY-MM-DD') : null,
      end: nextEnd ? nextEnd.format('YYYY-MM-DD') : null,
    })
  }

  const handleStartChange = (value: Dayjs | null): void => {
    setStart(value)
    emit(value, end)
  }

  const handleEndChange = (value: Dayjs | null): void => {
    setEnd(value)
    emit(start, value)
  }

  const handleClear = (): void => {
    setStart(null)
    setEnd(null)
    emit(null, null)
  }

  const hasValue = Boolean(start || end)

  return (
    <>
      <CustomFiltersMenu
        icon={<DateRangeIcon />}
        onOpenCustomFilters={onOpenFilters}
        count={count}
        badgeColor="icon.products"
      />
      <CustomFiltersModal isOpen={isOpen} anchorEl={anchorEl} handleCloseCustomFilters={onClose}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 280, gap: 1, p: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarTodayIcon sx={{ fontSize: 14, color: theme.palette.text.secondary }} />
                <Typography variant="caption" sx={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                  Active Between
                </Typography>
              </Box>
              {hasValue && (
                <IconButton size="small" onClick={handleClear} sx={{ p: 0.25 }}>
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl sx={{ flex: 1 }} size="small">
                <DatePicker
                  label="Start"
                  value={start}
                  onChange={handleStartChange}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        '& .MuiInputBase-root': { height: 32, fontSize: 12 },
                        '& .MuiInputLabel-root': { fontSize: 12 },
                      },
                    },
                  }}
                />
              </FormControl>
              <Typography sx={{ color: theme.palette.text.secondary, fontSize: 12 }}>–</Typography>
              <FormControl sx={{ flex: 1 }} size="small">
                <DatePicker
                  label="End"
                  value={end}
                  onChange={handleEndChange}
                  minDate={start || undefined}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        '& .MuiInputBase-root': { height: 32, fontSize: 12 },
                        '& .MuiInputLabel-root': { fontSize: 12 },
                      },
                    },
                  }}
                />
              </FormControl>
            </Box>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
              Shows products active at any point between these dates.
            </Typography>
          </Box>
        </LocalizationProvider>
      </CustomFiltersModal>
    </>
  )
}

export default ProductDateRangeFilter
